import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_PAYROLL_SPLIT } from "@/constants";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { PayrollsService } from "@/services/payrolls";
import { useQueryClient } from "@tanstack/react-query";
import { SplitIcon } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { PayrollConfirmButton } from "./PayrollConfirmButton";

export const PayrollSplitButton = memo(() => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const { payrollId } = usePayrollParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutationFn = async (payrollId: string) => {
    await PayrollsService.splitPayroll(payrollId);
  };

  const mutation = useMutationWithErrorHandling({
    errorTitle: t("payrolls.errors.split"),
    mutationFn,
    onSuccess: () => {
      toast({
        title: t("payrolls.success.split"),
      });
      queryClient.invalidateQueries({
        queryKey: [Module.Payrolls, payrollId],
      });
      navigate(
        generatePath(PATH_PAYROLL_SPLIT, {
          payrollId,
        }),
      );
    },
  });

  const onConfirm = useCallback(() => {
    mutation.mutateAsync(payrollId);
  }, [mutation, payrollId]);

  return (
    <PayrollConfirmButton
      icon={SplitIcon}
      translationKey="payrolls.splitPayroll"
      open={open || mutation.isPending}
      onOpenChange={setOpen}
      onConfirm={onConfirm}
      isPending={mutation.isPending}
    />
  );
});
